import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import APIServices from "../services/APIServices";
import Loader from "../components/Loader";
import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import appToast, { TOAST_TYPE } from "../utils/AppToast";
import {
  capitalizeFirstLetter,
  getformattedPhoneNumber,
  PAGE_LIMIT,
  selectLoader,
} from "../utils/Helpers";
import SidebarModal from "../components/SidebarModal";
import UserForm from "./sidebars/UserForm";
import { useAuth } from "../contexts/AuthContext";

const AdminList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Default to page 1
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [adminId, setAdminId] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const handler = setTimeout(() => {
      fetchAdminList(currentPage, searchQuery);
    }, 300);

    // Cleanup function to clear the timeout if dependencies change before delay
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchAdminList = async (page, searchQuery = "") => {
    setTableLoading(true);
    try {
      const response = await APIServices.get(
        `/admin/admin-users?page=${page}&limit=${PAGE_LIMIT}&search=${searchQuery}`
      );
      if (response) {
        setCurrentPage(response.currentPage);
        setTotalPages(response.totalPages);
        setAdminList(response.data);
      }
    } catch (err) {
      setError("Failed to fetch blog categories.");
      console.error("Error fetching blog categories:", err);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page && page !== currentPage) {
        fetchAdminList(page);
      }
    },
    [currentPage]
  );

  if (loading) {
    return <Loader />;
  }

  const handleToggle = async (userId, currentStatus) => {
    if (!isSuperAdmin) {
      appToast("Only super admins can change the status.", TOAST_TYPE.ERROR);
      return;
    }
    try {
      // Optimistically update the UI
      setAdminList((prevList) =>
        prevList.map((user) =>
          user._id === userId ? { ...user, loginStatus: !currentStatus } : user
        )
      );

      // Make API call
      let response = await APIServices.put(
        `/admin/update-login-status/${userId}`,
        {
          loginStatus: !currentStatus,
        }
      );
      if (response.message) {
        appToast(response.message, TOAST_TYPE.SUCCESS);
      }
    } catch (error) {
      console.error("Error updating login status:", error);
      appToast("Failed to update login status.", TOAST_TYPE.ERROR);

      // Revert UI change on failure
      setAdminList((prevList) =>
        prevList.map((user) =>
          user._id === userId ? { ...user, loginStatus: currentStatus } : user
        )
      );
    }
  };

  const closeAndUpdateList = () => {
    fetchAdminList(currentPage);
    setModalOpen(false);
  };
  const handleDelete = async (email) => {
    if (window.confirm("Are you sure you want to delete this Admin?")) {
      let payload = {
        email: email,
      };
      try {
        let response = await APIServices.delete(`/admin/delete`, payload);
        appToast(response.message, TOAST_TYPE.SUCCESS);
        fetchAdminList(1); // Refresh the list from page one after deletion
      } catch (error) {
        console.error("Error deleting admin:", error);
        appToast("Failed to delete admin", TOAST_TYPE.ERROR);
      }
    }
  };

  const handleEditAdmin = (id) => {
    setAdminId(id);
    setModalOpen(true);
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <section className="content adminUser eventtype">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card-top">
                    <div className="input-group">
                      <input
                        onChange={(e) => setSearchQuery(e.target.value)}
                        type="search"
                        className="form-control"
                        placeholder="Search Name"
                      />
                      <div className="input-group-append">
                        <i className="fa fa-search"></i>
                      </div>
                    </div>

                    {isSuperAdmin && (
                      <div className="btn-group-right">
                        {" "}
                        <button
                          onClick={() => {
                            setModalOpen(true);
                          }}
                          type="submit"
                          className="btn btn-blue btn-block"
                        >
                          + Add Admin
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="table-responsive">
                    <table
                      id="example"
                      className="table table-striped table-bordered"
                      cellSpacing="0"
                      width="100%"
                    >
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th colSpan={3}>Email</th>
                          <th colSpan={2}>Phone</th>
                          <th>Login Status</th>
                          {isSuperAdmin && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody
                        style={{
                          display: tableLoading
                            ? "table-caption"
                            : "table-row-group",
                        }}
                      >
                        {tableLoading ? (
                          selectLoader(50)
                        ) : adminList.length > 0 ? (
                          adminList?.map((user, index) => (
                            <tr key={user._id}>
                              <td>{user.fullName}</td>
                              <td colSpan={3}>{user.email}</td>
                              <td colSpan={2}>
                                {user.countryDialCode +
                                  getformattedPhoneNumber(user.phoneNumber)}
                              </td>
                              <td>
                                <label className="switch">
                                  <input
                                    // disabled={!isSuperAdmin}
                                    type="checkbox"
                                    checked={user.loginStatus}
                                    onChange={() =>
                                      handleToggle(user._id, user.loginStatus)
                                    }
                                  />
                                  <span className="slider round"></span>
                                </label>
                              </td>
                              {isSuperAdmin && (
                                <td>
                                  <ul className="actions">
                                    <li>
                                      <button
                                        className="btn btn-pen"
                                        onClick={() => {
                                          handleEditAdmin(user?._id);
                                        }}
                                      >
                                        <i className="fas fa-pen"></i>
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="btn btn-trash"
                                        onClick={() => {
                                          handleDelete(user.email);
                                        }}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </li>
                                  </ul>
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5">No data found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <ResponsivePagination
                      current={currentPage}
                      total={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SidebarModal
          extraClass={"edit-profile-modal client_modal"}
          show={modalOpen}
          onHide={() => {
            setModalOpen(false);
            setAdminId("");
          }}
          headerTitle={"Add New Admin"}
          // headerTitle={member ? "Edit Team Member" : "Add Team Member"}
        >
          <UserForm
            initialData={null}
            closeModal={closeAndUpdateList}
            adminId={adminId}
          />
        </SidebarModal>
      </div>
    </section>
  );
};

export default AdminList;
